const D = (...args) => console.log(...args)
const E = console.error
import {SubModule,IRoled,Role,Chain,ChainCell,Module, ChainCellItem,TItemChannel,ChainLane,ChainNode,IChainNode} from './model'
import type {IModule, IChainCell,IChainCellState,IChain,IChainLane,Roled,IChainCellItem,TStatusPair} from './model'
import { IMenuChoice } from './menu'
import { IMidiNote } from './models/midi'
import { Identity } from './models/id'
import { IArch } from './arch'

function clone<T>(o: T) {
    return JSON.parse(JSON.stringify(o)) as T
}

Array.prototype['tap'] = function(fn) {
    return this.map((item, index, array) => {
        typeof fn === 'function' && fn(item, index, array)
        return item
    })
}

export interface IMidiController {
    choices ({assign,assigned}:{
        assign (note?:IMidiNote) : void
        assigned?: {
            note: IMidiNote
            label: string
        }
    }) : IMenuChoice[]
    clear (note: IMidiNote) : void
    clearAll () : void
}

export interface IMatrixCellItemController {
    ab?: () => void
    mult?: () => void
    split?: () => void
    unbridge?: () => void
    ungroup?: () => void
    activate?: (channel:TItemChannel|undefined,state: boolean) => void
    delete?: (channel:TItemChannel) => void
    isolate?: (channel:TItemChannel) => void
    bridge?: (channel:TItemChannel) => void
    midi?: {
        assignment?: IMidiNote
        choices () : IMenuChoice[]
    }
}

export interface IDragged {
    module?: IModule
    role: IRoled
    chain?: string
    item?: {
        item: IMatrixCellItem
        lane: IMatrixLane
    }
    from?: {x: number, y: number}
    to?: {x: number, y: number}
}

export interface IDragObserver {
    start(dragged:IDragged) : void
    stop() : void
}

export interface IDragReceiver {
    chain: string
    lane: IMatrixLane
    item?: MatrixSlot
    fill?: boolean
    blank?: boolean
    role: IRoled
    zone: IDropZone
}

export interface IDropZone {
    upon?: boolean
    before?: boolean
    after?: boolean
    outerGap?: boolean
    channel: TItemChannel
}

// AB group
export interface IMatrixABGroup {
    id: string
    first: boolean
    last: boolean
    index: number
    gindex: number
    pindex: number
    count: number
    midi?: IMidiNote
}

export interface IMatrixItem extends IRoled {
    m2s?: IModule
    upM2S?: IModule 
}

export interface IMatrixCellItem extends IMatrixItem {
    fill: false
    _id: string
    cell: { id: string }
    lane: {
        id: string
        stereo?: boolean
        split?: boolean
    }
    item: number
    modules: { L?: IModule, R?: IModule }
    bridged?: boolean
    conjoined?: boolean
    stereo: boolean
    invalid?: boolean
    stub?: boolean
    active: TStatusPair
    bypassed?: TStatusPair
    abgroup?: IMatrixABGroup
    $source?: boolean
    sources: string[]
    chain: IChain
    split: boolean
    debug?: boolean
}

export class MatrixCellItem {
    static isActive (self: IMatrixCellItem) : boolean {
        return !!self.active.L
    }
}

export interface IMatrixFill extends IMatrixItem {
    fill: true
    lane: {
        id: string
        stereo: boolean
    }
    split: boolean
}

export type MatrixSlot = IMatrixCellItem | IMatrixFill

export interface IMatrixLane {
    lane: string
    cells: MatrixSlot[]
    split: boolean,
    stereo: boolean,
    [key: number]: undefined
}

export interface IMatrixChain {
    chain: IChain
    invalid: boolean
    // PURE stereo
    stereo?: boolean
    lanes: IMatrixLane[]
    [key: number]: undefined
}

interface IMatrixControlArgs2 {
    cell: string
}

interface IMatrixControlArgs3 extends IMatrixControlArgs2 {
    item: number
}

interface IMatrixControlArgs4 extends IMatrixControlArgs3 {
    channel: TItemChannel 
}

interface IMatrixControlArgsBypass extends IMatrixControlArgs3 {
    channel?: TItemChannel 
    active: boolean
}

export interface IMatrixController  {
    bridge (args: IMatrixControlArgs4) : void
    unbridge (args: IMatrixControlArgs3): void
    splitCell (args: IMatrixControlArgs3): void
    deleteLane ({lane}:{lane:number}) : void
    // UNGROUP cell
    ungroup (args: IMatrixControlArgs2) : void
    // DELETE module
    deleteItem (args: IMatrixControlArgs3 & {channel?: TItemChannel}) : void
    // COPY module into a fresh chain (leaves the original in place)
    isolateItem (args: IMatrixControlArgs3 & {channel?: TItemChannel}) : void
    // CREATE AB-group from module
    // id => cell ID
    ab (args: IMatrixControlArgs2) : void
    // BYPASS module and its dependents
    // id => module ID
    activate (args: IMatrixControlArgsBypass) : void
    // MULT cell
    mult (args: IMatrixControlArgs2) : void
    // INITIATE intra/extra cell/item/channel drag
    onDrag (dragged: IDragged) : void
    onDrop ({
        receiver,
        dropped,
        copy,
    }: {
        receiver: IDragReceiver
        dropped: IDragged
        copy?: boolean
    }) : void
    midi: {
        // ASSIGN MIDI note to AB-group "roll"
        abgroup: {
            choices (cell: string) : IMenuChoice[]
        }
    }
}

const extractRole = ({proc=false,dest=false,source=false}:{proc?:boolean,dest?:boolean,source?:boolean}) : IRoled => ({
    proc,dest,source,
})

export function ChainMatrixControl ({set:$set,chain:_chain,midi,idgen,arch,onLocked,onIsolate}
    : {
        set:(chain:IChain,mutated:boolean,crossChainRemove?:{chainId:string,cellId:string,item:number})=>void
        midi: IMidiController
        chain:IChain
        idgen:() => string
        arch:IArch
        onLocked?:() => void
        // COPY a module into a fresh chain. Lives at the collection level (needs addChain),
        // so it's injected here where the chains-collection controller is in scope.
        onIsolate?:(module:IModule) => void
    }) : IMatrixController
{
    function get() : IChain {
        return clone(_chain)
    }

    function newCell ({id,item,role,state,split} : {id?: string,item?:IChainCellItem,role:Roled,state:IChainCellState,split:boolean})
    : IChainCell
     {

        return ChainCell.create({
            id:id ?? idgen(),
            items: item ? [item] : undefined,
            state,
            ...role,
            split,
        })
    }

    /**
     * Fixup chain after modification
     */
    function set (chain:IChain,mutated:boolean=false,crossChainRemove?:{chainId:string,cellId:string,item:number}) {
        if (_chain.state.locked) { D('CHAIN-SET: blocked (locked)', _chain.id); onLocked?.(); return }
        const cells = {}
        chain.lanes.forEach(lane =>
            lane.cells.forEach((cell,i,lane) => {
                if (cells[cell.id]) {
                    // FIXME this is fragile
                    // replace instances of "downstream" (i.e. sync) cell copies with "reference"
                    // ...this allows naive modification of lane's source
                    lane[i] = cells[cell.id]
                }
                else {
                    cells[cell.id] = cell
                }
            })
        )
        $set(chain,mutated,crossChainRemove)
    }

    /**
     * FIND chain cell instance (and accompanying lane)
     */
    function findCell ({chain,cell:id}:{chain:IChain,cell:string})
        : {index:number,cell:IChainCell,lane:IChainLane} | undefined
    {
        const lane = chain.lanes.find(lane => lane.cells.some(cell => id === cell.id))
        if (!lane) {
            D('UNABLE to FIND lane for CELL',id,'in CHAIN',chain.id)
            return undefined
        }
        else {
            const index = lane.cells.findIndex(cell => id === cell.id)
            // LOCATE cell's home LANE
            const cell = lane.cells[index]
            return {index,cell,lane}
        }
    }

    /**
     * EXTRACT downstream dependents (i.e. lanes), direct and indirect, of mult source
     */
    function extractMultGraph ({chain,cell}:{chain:IChain,cell:IChainCell}) : IChainLane[] | undefined
    {
        // LANES to be extracted
        const lanes:Array<{lane:IChainLane,i:number}> = []
        function extract (cell:IChainCell) {
            chain.lanes.forEach((lane,i) => {
                if (lane.cells[0].id === cell.id) {
                    // RECURSIVELY follow other multed-sources of lane
                    for (const cell of lane.cells.slice(1)) {
                        extract(cell)
                    }
                    if (i !== 0) lanes.push({lane,i})
                }
            })
        }
        extract(cell)
        return lanes.length ? lanes.sort((a,b) => a.i - b.i).map(({lane}) => lane) : undefined
    }

    /**
     * DELETE downstream dependents (i.e. lanes), direct and indirect, of mult source
     */
    function deleteMultGraph ({chain,cell} : {chain:IChain,cell:IChainCell}) : IChainLane[] | undefined
    {
        // LANES to be DELETED
        const lanes = extractMultGraph({chain,cell})
        if (!lanes) return
        // index of graph lane within chain
        const index = chain.lanes.findIndex(({id}) => id === lanes[0].id)
        // DELETE all graph lanes ...presumes extracted lanes are sequential
        chain.lanes.splice(index,lanes.length)
        return lanes
    }

    /**  
     * DELETE cell item. Fixes up any AB-groups
     * returns deleted item and any dependency "graph" of deleted cell, if any
     */
    function delocate ({chain,cell:$cell,item:itemIdx,channel} : {chain:IChain,cell:string,item:number,channel?:TItemChannel})
    : {graph?: IChainLane[],cell: IChainCell} | undefined
    {
        // FIND respective cell for cell ID
        function findCell ({chain,cell}:{chain:IChain,cell:string}) : {index:number,cell?:IChainCell,lane?:IChainLane} {
            for (const lane of chain.lanes) {
                const index = lane.cells.findIndex(_ => _.id === cell)
                if (index !== -1) return {index,cell:lane.cells[index],lane}
            }
            return {index:-1}
        }

        const found = findCell({chain,cell:$cell})
        if (!found.cell || !found.lane) {
            E('delocate: CELL/LANE not found',$cell)
            return
        }
        const {index,cell,lane} = found
        const {proc} = cell
        const {items} = cell
        const item = items[itemIdx]
        // DELETE entire item? (NON-SPLIT lane OR CONJOINED stereo module OR only populated channel of split cell)
        const deleteItem = !item.split || ChainCellItem.isConjoined(item) || !channel || ChainCellItem.isPartial(item) && item[channel]
        // AB-CELL
        if (cell.ab) {
            // DELETE entire item?
            if (deleteItem) {
                // DELETE item
                items.splice(itemIdx,1)[0]
                // DELETE cell?
                if (items.length === 1) {
                    if (ChainCellItem.isEmpty(items[0])) {
                        // EMPTY PROC? DELETE *cell*
                        if (proc) {
                            // PRUNE off mults (into subgraph)
                            const graph = deleteMultGraph({chain,cell})
                            // DELETE cell
                            lane.cells.splice(index,1)
                            const $cell = newCell({
                                id: cell.id,
                                item,
                                role: extractRole(cell),
                                state: cell.state,
                                split: cell.split,
                            })
                            // UPDATE cell instance in subgraph
                            // XXX only because we are not representing chain as true graph (yet)
                            /*
                            if (graph) {
                                graph.forEach(lane => {
                                    lane.cells.forEach((c,i) => {
                                        // REPLACE original cell with updated version
                                        if (c.id === cell.id) {
                                            lane.cells[i] = $cell
                                        }
                                    })
                                })
                            }
                                */
                            return {
                                cell: $cell,
                                graph,
                            }
                        }
                        // EMPTY S/D? STUB out
                        else {
                            cell.ab = false
                            cell.state.active = {L:0,R:0}
                        }
                    }
                    // APPEND stub, reset ACTIVE state
                    else {
                        items[1] = ChainCellItem.createStub(cell.split)
                        cell.state.active = proc ? {} : {L:0,R:0} 
                    }
                }
                // XXX state?
                return {
                    cell: newCell({
                        item,
                        role: extractRole(cell),
                        state: cell.state,
                        split: cell.split,
                    })
                }
            }
            else {
                const $item = clone(item)
                item[channel] = undefined
                return {
                    cell: newCell({
                        item:$item,
                        role: extractRole(cell),
                        state: cell.state,
                        split: cell.split,
                    })
                }
            }
        }
        else {
            // DELETE entire item?
            if (deleteItem) {
                // PROC - DELETE cell
                if (proc) {
                    // PROC - delete and capture mult graph
                    const graph = deleteMultGraph({chain,cell})
                    lane.cells.splice(index,1)
                    return {graph,cell}
                }
                // S/D - STUB cell
                else {
                    lane.cells[index] = ChainCell.createStub(cell)
                    return {
                        cell: newCell({
                            item,
                            role: extractRole(cell),
                            state: cell.state,
                            split: cell.split,
                        })
                    }
                }
            }
            else {
                const $item = clone(item)
                item[channel] = undefined
                return {
                    cell: newCell({
                        item:$item,
                        role: extractRole(cell),
                        state: cell.state,
                        split: cell.split,
                    })
                }
            }
        }
    }

    return {
        midi: {
            abgroup: {
                choices ($cell) {
                    const chain = get()
                    const found = findCell({chain,cell:$cell})
                    if (!found || !found.cell.ab) return []
                    const {cell} = found
                    const cells = Chain.cells(chain)
                    const index = cells
                        .filter(({ab}) => ab)
                        .findIndex(({id}) => id === cell.id)
                    const assigned = cell.midi
                        ?  {
                            label: `${chain.state.name} → ABGROUP #${index + 1}`,
                            note: cell.midi,
                        }
                        : undefined
                    const assign = (note?:IMidiNote) => {
                        cell.midi = note
                        set(chain)
                    }
                    return midi.choices({assigned,assign})
                }
            }
        },
        bridge ({cell:$cell,item,channel}) {
            const chain = get()
            D('CHAIN-CELL-BRIDGE',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell} = found
            if (!cell.items[item][channel]) {
                D("bridge: GROUP is undefined",$cell,item,channel)
                return
            }
            else {
                const module = cell.items[item][channel] as IModule
                // BRIDGE stereo module case
                const $module = module.stereo ? SubModule.split(module)!.L : module
                // XXX AB group?!
                cell.items[item].L = $module
                cell.items[item].R = $module
                set(chain,true)
            }
        },
        unbridge ({cell:$cell,item=0}) {
            const chain = get()
            D('CHAIN-CELL-UNBRIDGE',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell} = found
            // XXX AB group?!
            cell.items[item].R = undefined
            set(chain,true)
        },
        splitCell ({cell:$cell,item=0}) {
            const chain = get()
            D('CHAIN-CELL-SPLIT',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell} = found
            // SPLIT and replace modules
            // XXX verify stereo
            const module: IModule = cell.items[item].L!
            const {L,R} = SubModule.split(module)!
            cell.items[item].L = L
            cell.items[item].R = R
            set(chain,true)
        },
        deleteLane ({lane}) {
            const chain = get()
            D('CHAIN-LANE-DELETE',chain?.id)
            // DELETE mult deps (if any)
            chain.lanes[lane]
                .cells
                .slice(1)
                .filter(({proc}) => proc)
                .forEach(cell => deleteMultGraph({chain,cell}))
            // DELETE model lane
            chain.lanes.splice(lane,1)
            set(chain,true)
        },
        // UNGROUP cell
        ungroup ({cell:$cell}) {
            const chain = get()
            D('CHAIN-CELL-UNGROUP',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell} = found
            // UNGROUP remaining
            cell.ab = false
            if (cell.proc) {
                cell.state.active = {}
            }
            else {
                cell.state.active = {L: 0, R: 0}
            }
            cell.items.length = 1
            set(chain,true)
        },
        // DELETE CELL ITEM
        deleteItem ({cell,item,channel}) {
            // MODEL cell
            const chain = get()
            D('CHAIN-CELL-DELETE',chain?.id,cell,channel)
            const found = findCell({chain,cell})
            if (!found) return

            // REMOVING M2S module? UNSPLIT everything downstream
            if (ChainCell.hasM2S(found.cell)) {
                const graph = Chain.graph(chain)!
                const m2s = ChainNode.findCell(graph,found.cell.id)!
                ChainCell.unsplit(m2s!.cell)
                ChainNode.visitDown(m2s,visit)

                function visit (node?:IChainNode) : boolean {
                    if (node) {
                        ChainCell.unsplit(node.cell)
                    }
                    return true
                }
            }

            delocate({
                chain,
                cell,
                item,
                channel,
            })
            set(chain,true)
        },
        // COPY CELL ITEM into a fresh chain (original untouched — read-only here)
        isolateItem ({cell,item,channel}) {
            const chain = get()
            const found = findCell({chain,cell})
            if (!found) return
            const cellItem = found.cell.items[item]
            // PREFER the clicked channel; fall back to L (mono / non-split cell)
            const module = (channel && cellItem?.[channel]) ?? cellItem?.L
            if (!module) { D('CHAIN-CELL-ISOLATE: no module',chain?.id,cell,channel); return }
            D('CHAIN-CELL-ISOLATE',chain?.id,cell,module.id,channel)
            onIsolate?.(module)
        },
        // CREATE AB-group from module
        // id => cell ID
        ab ({cell:$cell}) {
            // MODEL cell
            const chain = get()
            D('CHAIN-CELL-AB',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {index,cell,lane} = found
            if (!cell) return
            // ALREADY group?!
            if (cell.ab) return

            const ab = ChainCell.createAB(cell)

            lane.cells[index] = ab
            set(chain,true)
        },
        // SET active state of item channel
        activate ({cell:$cell,item,channel,active}) {
            const chain = get()
            D('CHAIN-CELL-ACT',chain?.id,$cell,channel ?? 'M')
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell} = found
            if (!cell) return
            const {state} = cell

            /*
            // ACTIVATE cell item of SRC/DEST (implies AB-group)
            if (!proc) {
                // IGNORE requests to deactivate an item (which is non-sensical)
                if (toggle || active) {
                    state.active = {
                        L: item,
                        R: item,
                    }
                }
            }
            // (DE)ACTIVATE cell item of PROC
            else {
            */
            // MODIFY both channels
            if (!channel) {
                state.active = active ? {L: item, R: item} : {}
            }
            // SPECIFIC channel
            else {
                state.active[channel] = active ? item : undefined
            }
            set(chain,true)
        },
        // MULT cell
        mult ({cell:$cell}) {
            const chain = get()
            D('CHAIN-CELL-MULT',chain?.id,$cell)
            const found = findCell({chain,cell:$cell})
            if (!found) return
            const {cell,lane} = found
            ////////////////////////////////////////////////////////////////
            //  WHERE DO WE INSERT THE NEWLY MULT'ED LANE?
            ////////////////////////////////////////////////////////////////
            // We need to find next lane source that originates before the block
            // being multed:
            // INSPECT each lane source occurring after prospective mult's lane
            //   Does first occurrence of its source lie after mult's?
            //    - yes - keep searching
            //    - no - insert new mult lane here
            //
            // FLATTEN chain to array of cell IDs
            const cells = chain.lanes.flat().map(({cells})=>cells).flat().map(({id}) => id)
            const laneIdx = chain.lanes.findIndex(_ => lane.id === _.id)
            // COLLECT lane source IDs
            const sources = chain
                .lanes
                .slice(laneIdx + 1)
                // LANE SOURCE ID (first cell of lane)
                .map(lane => lane.cells[0].id)
                .map(src  => cells.findIndex(_ => _ === src))

            // INDEX of mult as found in cell ID colletion
            const index = cells.findIndex(id => cell.id === id)

            // INSERTION INDEX
            // DISCOVER first instance of a lane source that is *previous* to this cell
            // this is a lane source we are directly below, visually
            // (for instance, all lane sources are beneath the chain's source)
            const insert = sources.length > 0
                ? sources.findIndex(_ => _ <= index)
                : 0
            const $insert = insert === -1 ? chain.lanes.length : insert + laneIdx + 1
            // EMPTY LANE: source + dest
            chain.lanes.splice(
                $insert,0,
                ChainLane.create({
                    id:idgen(),
                    split:cell.split,
                    cells: [
                        {...cell},
                        ChainCell.create({
                            id: idgen(),
                            dest: true,
                            items: [ChainCellItem.createStub(cell.split)],
                            split: cell.split,
                        }),
                    ]
                })
            )
            set(chain,true)
        },
        // intra/extra-chain cell drag start
        onDrag (matrixCell)
        {
            /*
            //const $cell = matrixCell.cell.id
            const chain = get()
            // all mutations take place in copy
            const $chain = clone<IChain>(chain)
            return {
                cell: matrixCell,
                chain: {
                    id:chain.id,
                    // cell + graph
                    ...(clone(delocate({chain:$chain,cell:$cell,item:matrixCell.item}))),
                },
                // REMOVE dragged cell from this chain (if drag was successful)
                commit: () => {
                    const $chain = clone<IChain>(chain)
                    delocate({chain:$chain,cell:$cell,item:matrixCell.item}),
                    // COPY modified lanes back to canonical chain reference
                    chain.lanes.splice(0,chain.lanes.length,...$chain.lanes)
                    set(chain)
                }
            }
                */
        },
        // (BROWSER|CHAIN)_NODE drop
        onDrop ({
            receiver,
            dropped,
            copy,
        })
        {
            if (!receiver.item) return

            const dropModule:IModule|undefined = dropped.module
            const dropCell = dropped?.item?.item
            // CROSS-CHAIN: treat cell's module as a browser module drop
            const crossChain = !!dropCell && dropped.chain !== _chain.id
            const $dropModule = dropModule || (crossChain ? (dropCell.modules.L || dropCell.modules.R) : undefined)

            const $chain = get()
            // REQUIRES splitting chain to accept module?
            const requiredSplit = !!$dropModule && !Chain.isSplit($chain) && !$dropModule.mono
            // MUTATIONS take place in copy
            const chain = 
                requiredSplit 
                ? Chain.split(clone<IChain>($chain))
                : clone<IChain>($chain)

            const matrixCell = receiver.item as IMatrixCellItem
            const foundCell = !matrixCell.fill ? findCell({chain,cell: matrixCell.cell.id}) : undefined
            if (!matrixCell.fill && !foundCell) {
                E('onDrop: receiver CELL not found',receiver)
                return
            }
            // RECEIVER LANE instance (mutable)
            const lane = chain.lanes.find(({id}) => id === matrixCell.lane.id)
            if (!lane) {
                E('onDrop: receiver LANE not found',receiver)
                return
            }
            const {zone} = receiver
            const laneIndex = chain.lanes.findIndex(({id}) => id === matrixCell.lane.id)

            // RECORD dropped is M2S module
            const m2s = !!$dropModule?.m2s

            // RECEIVER drop channel (LEFT-side or RIGHT-side)
            const channel = m2s ? 'L' : zone.channel

            // RECEIVER split
            const split = matrixCell.split || requiredSplit || m2s

            // !!! CAPTURE receiver's lane instance before any modification of chain's graph
            const recv : {
                cell?: IChainCell
                bridged: boolean
                modules: {L?: IModule, R?: IModule}
                lane: IChainLane
                laneIndex: number
                item: number
                index: number
                zone: IDropZone
                channel: TItemChannel
                split: boolean
            } =
                foundCell
                // MatrixCellItem
                ? {
                    ...foundCell,
                    bridged: !!matrixCell.bridged,
                    modules: matrixCell.modules,
                    lane,
                    laneIndex,
                    zone: zone,
                    item: matrixCell.item,
                    channel,
                    split,
                }
                // MatrixFill
                : {
                    cell: undefined,
                    index: -1,
                    bridged: false,
                    modules: {L: undefined} as IChainCellItem,
                    lane,
                    laneIndex,
                    zone: zone,
                    item: 0,
                    channel,
                    split,
                }

            // RECEIVER (CELL) INDEX can/will be adjusted
            let index = recv.index
            // intra-chain cell drag (excludes cross-chain — those take the $dropModule path)
            const intraDrop = !!dropCell && !crossChain
            const intraABReorder =
                !!recv.cell?.ab
                && !!dropCell
                && dropCell.cell.id === recv.cell.id
                && !!(zone.before || zone.after || zone.upon)
                && !zone.outerGap

            // ── INTRA-AB REORDER / REPLACE (early-out) ──
            // Handles reorder (before/after) and replace (upon) within the same
            // AB-group cell without creating a throwaway newCell().
            if (intraABReorder && recv.cell) {
                const {before, upon} = recv.zone
                const [moved] = recv.cell.items.splice(dropCell.item, 1)
                if (upon) {
                    const target = dropCell.item < recv.item ? recv.item - 1 : recv.item
                    recv.cell.items[target] = moved
                    if (recv.cell.items.length === 1) {
                        recv.cell.items[1] = ChainCellItem.createStub(recv.split)
                    }
                } else {
                    const insert = before
                        ? recv.item - (dropCell.item < recv.item ? 1 : 0)
                        : recv.item + (dropCell.item > recv.item ? 1 : 0)
                    recv.cell.items.splice(insert, 0, moved)
                }
                Chain.reconcile(chain)
                const lanes = chain.lanes
                $chain.lanes.splice(0, lanes.length, ...lanes)
                set($chain, true)
                return
            }

            /*
            // INTRA-NODE DND - remove dropped node from original slot
            if (dropModule && dropped.chain === chain.id) {
                const root = Chain.graph(chain)!
                const node = ChainNode.findCell(root,cell => {
                    if (cell.items.findIndex(({L}) => L && L?.id === dropModule.id) >= 0) {
                        return true
                    }
                    else if (cell.split && cell.items.findIndex(({R}) => R && R?.id === dropModule.id) > 0) {
                        return true
                    }
                    else {
                        return false
                    }
                })
                D('DROP CELL',node)
                if (node) {
                    const {cell} = node
                    const item  = cell.items.findIndex(({L}) => L && L?.id === dropModule.id)
                    if (item >= 0) {
                        D('DELTE CELL L')
                        delete cell.items[item].L
                    }
                    else if (cell.split) {
                        const item  = cell.items.findIndex(({R}) => R && R?.id === dropModule.id)
                        if (item >= 0) {
                            D('DELTE CELL R')
                            delete cell.items[item].R
                        }
                    }
                }
            }
            */
            if ($dropModule) {
                D('CHAIN-NODE-DROP',$chain?.id,$dropModule.id)
            }
            const {cell,graph} : {cell?:IChainCell, graph?:IChainLane[]} =
                $dropModule
                ?  {
                    cell: newCell({
                        role: extractRole($dropModule),
                        state: {
                            active: Chain.nodes(chain).some(node => Module.conflicting($dropModule,node,arch))
                                // OFF - dropped node conflicts other chain nodes
                                ? {}
                                 // DEFAULT to ON, both channels (XXX ...is that a good idea?)
                                : {L: 0, R: 0},
                        },
                        split,
                        item:
                            ChainCellItem.create(
                                split
                                ? (
                                    $dropModule.stereo
                                    // "conjoined" stereo module
                                    ? {L: $dropModule, R: $dropModule, split: true}
                                    : recv.channel === 'L'
                                    // dropped in LEFT channel
                                    ? {
                                        L: $dropModule,
                                        R: recv.zone.upon
                                            ? (
                                                (recv.bridged || recv.modules.R?.id ===  $dropModule.id)
                                                ? undefined
                                                : decompModule('R',recv.modules.R)
                                            )
                                            : undefined,
                                        split: true,
                                    }
                                    // dropped in RIGHT channel
                                    : {
                                        L: recv.zone.upon
                                            ? (
                                                recv.modules.L?.id !== $dropModule.id
                                                ? decompModule('L',recv.modules.L)
                                                : undefined
                                            )
                                            : undefined,
                                        R: $dropModule,
                                        split: true,
                                    }
                                )
                                : {L: $dropModule, split: false})
                    })
                }
                : intraDrop
                    // remove (dropped) source cell and its downstream deps (mults) from chain
                    ? delocate({chain,cell:dropCell.cell.id,item:dropCell.item}) || {}
                : {}

            if (!cell) {
                E('onDrop: dropped CELL not found',dropped)
                return
            }

            // !!! REFRESH receiver's lane index - may have been affected by deletion of dropped cell above
            if (recv.cell && intraDrop) index = recv.lane.cells.findIndex(_ => _.id === recv.cell!.id)

            // DEST fill is receiver
            if (matrixCell.fill) {
                // ONLY a processor may be dropped in a matrix fill
                if (!cell.proc) return
                // FIND 
                const index = recv.lane.cells.findIndex(_ => _.dest)
                // INSERT before lane dest
                recv.lane.cells.splice(index,0,cell)
                if (graph) relocateMultGraph({lane:recv.lane,chain,index,graph})
            }
            else if (recv.cell) {
                const item = cell.items[0]
                // receiver is last item of AB-group?
                const last = recv.item === recv.cell.items.length - 1
                const {before,upon,after} = recv.zone
                // dropped directly upon/below lane (non-chain) source
                if (recv.laneIndex > 0 && index === 0) {
                    // proc can be dropped directly beneath lane source
                    if (cell.proc && !(after && (!recv.cell.ab || last))) {
                        E('onDrop: ONLY proc can be dropped directly preceding lane source, non-AB cell *or* AB last')
                        return
                    }
                    // INSERT below lane source
                    recv.lane.cells.splice(index + 1,0,cell)
                    // XXX if M2S unsplit source
                }
                // INSERT item/module into AB-group here
                else if (recv.cell.ab) {
                    const recvCell = recv.cell
                    const first = recv.item === 0
                    const {proc,dest} = recvCell

                    if (cell.proc && zone.outerGap) {
                        const insertAt = recv.lane.cells.findIndex(_ => _.id === recvCell.id)
                            + (zone.after ? 1 : 0)
                        recv.lane.cells.splice(insertAt, 0, cell)
                        if (graph) {
                            relocateMultGraph({chain, lane: recv.lane, index: insertAt, graph})
                        }
                    }
                    // INSERT item/module into AB-group
                    else if ((upon || before || after) && Role.isSame(cell,recv.cell)) {

                        // REPLACE AB item
                        if (upon) {
                            // XXX bridged?
                            recv.cell.items[recv.item] = item
                            // ADD STUB
                            if (recv.cell.items.length === 1) {
                                recv.cell.items[1] = ChainCellItem.createStub(recv.split)
                            }
                        }
                        // INSERT AB item
                        else {
                            recv.cell.items.splice(recv.item + (before ? 0 : 1),0,item)
                        }
                    }
                    // INSERT item/module above/before AB-group (only processors)
                    else if (cell.proc && ((first && before && (proc || dest)) || (last && after && index === 0))) {
                        // INSERT cell above
                        if (before) {
                            recv.lane.cells.splice(index,0,cell)
                        }
                        // INSERT cell below
                        else {
                            recv.lane.cells.splice(index + 1,0,cell)
                        }
                        if (graph) {
                            D('onDrop: relocating mult graph after AB insert')
                            relocateMultGraph({chain,lane:recv.lane,index:index + (before ? 0 : 1),graph})
                        }
                    }
                    else {
                        const reason = !Role.isSame(cell,recv.cell)
                            ? 'AB-group ROLE MISMATCH'
                            : 'missing drop condition handler'
                        E(`onDrop: *SHOULD NOT BE HERE* ${reason}`)
                        return
                    }
                }
                // INSERT/REPLACE CELL item
                else {
                    // INSERTS only for procs
                    if (!(upon && Role.isSame(cell,recv.cell) || !upon && cell.proc)) {
                        D('drop: INSERT for processors only')
                        return
                    }
                    // REPLACE cell
                    if (upon) {
                        // MUST REPLACE NODE this way because it could be a mult source
                        const root = Chain.graph(chain)!
                        const replaced = recv.lane.cells[index]
                        const node = ChainNode.findCell(root,replaced.id)
                        if (node) {
                            // M2S SCENARIO!
                            // WE have dragged a mono proc cell (somewhere above M2S) into a split/stereo proc cell
                            if (!cell.split && recv.split) {
                                if (cell.proc) {
                                    node.cell.items[0][channel] = cell.items[0].L
                                }
                                else {
                                    // XXX ????
                                    E('NON-PROC DRAG MONO-CELL-to-SPLIT-CELL')
                                }
                            }
                            else {
                                node.cell = cell
                            }
                            chain.lanes = Chain.ungraph(root)
                            recv.lane = chain.lanes[recv.laneIndex]
                        }
                        else {
                            D(`CANNOT FIND NODE for cell ${replaced.id}`)
                            return
                        }
                    }
                    // INSERT cell above/below receiver
                    else {
                        recv.lane.cells.splice(index + (after ? 1 : 0),0,cell)
                    }
                    // if cell is mult source we must also relocate its depedents
                    if (graph) {
                        relocateMultGraph({chain,lane:recv.lane,index:index + (after ? 1 : 0),graph})
                    }
                }
            }

            Chain.reconcile(chain)
            const lanes = chain.lanes
            // COPY modified lanes back to canonical chain reference
            $chain.lanes.splice(0,lanes.length,...lanes)
            // CROSS-CHAIN MOVE: source deletion batched into same transaction.
            // If user cancels conflict dialog, full revert undoes both target insertion and source deletion.
            const crossChainRemove = crossChain && !copy
                ? { chainId: dropped.chain!, cellId: dropCell.cell.id, item: dropped.item!.item.item }
                : undefined
            set($chain,true,crossChainRemove)

            // DECOMPOSE stereo module into L or R channel (channel opposite of receiver offset)
            function decompModule (channel:TItemChannel,module?:IModule) : IModule | undefined {
                if (!module) return undefined
                return module.stereo
                    ? SubModule.split(module)![channel]
                    : module 
            }

            function relocateMultGraph ({lane,chain,index,graph} 
                : {
                    lane: IChainLane
                    chain: IChain
                    index: number
                    graph: IChainLane[]
                })
            {
                let $graph: IChainLane[] | undefined
                // is there a mult source beneath us in the target lane?
                // ...yes? we need to place dropped cell's graph directly after its graph
                lane.cells.slice(index).find(cell => $graph = extractMultGraph({chain,cell}))
                if ($graph) {
                    // FIND index of right-most lane of mult graph (insert index of dragged cell's graph)
                    const index = chain.lanes.findIndex(({id}) => id === $graph![$graph!.length - 1].id)
                    // INSERT mult graph just after
                    chain.lanes.splice(index + 1,0,...graph)
                }
                else {
                    const index = chain.lanes.findIndex(({id}) => id === lane.id)
                    chain.lanes.splice(index + 1,0,...graph)
                }
            }
        },
    }
}

// TRANSFORM "data" model chain to "view" model chain
// XXX document chain view model conventions
export function ModelXform ({chain,debug,arch}: {chain:IChain,debug?:boolean,arch:IArch}) : IMatrixChain {
    try {
        // LONGEST lane (sans dests)
        let $LH = 0
        // LONGEST dest AB-group
        let $GH = 0

        /**
         * FORCE chain inactivation if invalid
         * XXX really this shouldn't need to be here, this should be policed by higher-level
         * business logic
         */
        if (Chain.invalid(chain,arch)) {
            delete chain.cond.active
        }

        /**
         * INDEX of ACTIVE NODES that conflict with any other ACTIVE NODE in chain
         */
        const conflicts =
            Chain.activeNodes(chain)
            .filter((a,_,nodes) => nodes.some(b => Module.conflicting(a,b,arch)))
            .reduce((p,c) => {
                p[c.id] = c
                return p
            },{})


        const idcache = {}
        // GENERATE ID for cell + cell item instance
        const getId = (cell:string,item:number) => idcache[`${cell}${item}`] || (idcache[`${cell}${item}`] = Identity.create())

        // TRACK rightmost-lane of cell instance (i.e. where each terminates)
        const tracks : Map<string,number> = new Map()

        const m2sCells: Record<string,IModule> = {}
        const m2sUpCells: Record<string,IModule> = {}
        const m2sDownCells: Record<string,IChainCell> = {}
        const bypassed: Record<string,TStatusPair> = {}
        Chain
            .cells(chain)
            .forEach(cell => {
                bypassed[cell.id] = Chain.bypassState(chain,cell)
            })

        /**
         * INDEX any cell upstream OR downstream of an M2S module.
         * Key data for determining if an M2S module can be placed on/after/before a chain cell.
         */
        function indexM2SCells (chain:IChain)  {
            const graph = Chain.graph(chain)
            const cells = Chain.cells(chain)
            if (graph) {
                cells.forEach(cell => {
                    const node = ChainNode.findCell(graph,cell.id)
                    if (node) {
                        // XXX for whatever reason results are exactly the opposite of what I expected,
                        // hence the switching of up/down. Needs investigation.
                        // SEARCH UPSTREAM
                        ChainNode.visitDown(node,visitUp)
                        // SEARCH DOWNSTREAM
                        ChainNode.visitUp(node,visitDown)
                    }
                    function visitUp (node?:IChainNode) : boolean {
                        const m2s = node && ChainCell.hasM2S(node.cell)
                        if (m2s) {
                            // RECORD cell is upstream of M2S module
                            m2sCells[node.cell.id] = m2s
                            m2sUpCells[node.cell.id] = m2s
                            return false
                        }
                        else {
                            return true
                        }
                    }
                    function visitDown (node?:IChainNode) : boolean {
                        const m2s = node && ChainCell.hasM2S(node.cell)
                        if (m2s) {
                            // RECORD cell is downstream of M2S module
                            m2sCells[cell.id] = m2s
                            m2sDownCells[cell.id] = node.cell
                            return false
                        }
                        else {
                            return true
                        }
                    }
                })
            }
        }

        indexM2SCells(chain)

        // INDEX: CHAIN CELLS by ID
        const cells: Record<string,IChainCell> = {}

        // INSTANTIATE a MATRIX CELL ITEM
        function instantiate (
            {lane,cell,cellIndex,item,itemIndex,laneIndex}
            : {
                cell:IChainCell
                cellIndex: number
                item:IChainCellItem
                itemIndex:number
                laneIndex:number
                lane:IChainLane
            })
            : IMatrixCellItem {
            // COLLECT VIEW metadata
            const {source,proc,dest,state:{active}} = cell
            const _id = getId(cell.id,itemIndex)
            tracks.set(_id,laneIndex)
            cells[cell.id] = cell
            // XXX rather hacky business...
            // We mark modules invalid here because we presume
            // they are ephemeral copies and we don't need to worry about
            // synchronizing this property later (to reflect changes to valid state)
            const conflictPorts  = {
                from: {},
                to: {},
            }
            // COLLECT conflicting node ports
            Chain.activeNodes(chain)
                .reduce((p:{from:{},to:{}},node) => {
                    const ports = Module.ports(node,arch)
                    ports.from.forEach(
                        port => {
                            // MARK any conflicting port(s) - direct or *INDIRECT*
                            Object
                                .keys(p.from)
                                .filter(b => arch.ports.isConflict(port,b))
                                .forEach(conf => conflictPorts.from[conf] = conflictPorts.from[port] = 1)
                            // MARK port as observed
                            p.from[port] = 1
                        })
                    ports.to.forEach(
                        port => {
                            // MARK any conflicting port(s) - direct or *INDIRECT*
                            Object
                                .keys(p.to)
                                .filter(b => arch.ports.isConflict(port,b))
                                .forEach(conf => conflictPorts.to[conf] = conflictPorts.to[port] = 1)
                            // MARK port as observed
                            p.to[port] = 1
                        })
                    return p
                },{from:{},to:{}})

            if (item.L) {
                item.L.invalid = Module.invalid(item.L,arch)
                    || Module.ports(item.L!,arch).from.some(port => conflictPorts.from[port])
                    || Module.ports(item.L!,arch).to.some(port => conflictPorts.to[port])
            }
            if (item.R) {
                item.R.invalid = Module.invalid(item.R,arch)
                    || Module.ports(item.R!,arch).from.some(port => conflictPorts.from[port])
                    || Module.ports(item.R!,arch).to.some(port => conflictPorts.to[port])
            }
            return {
                _id,
                debug,
                fill: false,
                cell: {
                    id: cell.id,
                },
                item: itemIndex,
                lane: {
                    id: lane.id,
                    split: ChainLane.isSplit(lane),
                    stereo: ChainLane.isStereo(lane),
                },
                split: cell.split,
                stereo: ChainCellItem.isStereo(item),
                modules: item,
                bridged: ChainCellItem.isBridged(item),
                conjoined: ChainCellItem.isConjoined(item),
                source,proc,dest,
                active: {
                    L: active.L === itemIndex,
                    R: active.R === itemIndex,
                },
                $source: cellIndex === 0,
                sources: [],
                stub: ChainCellItem.isEmpty(item),
                chain: chain,
                m2s: m2sCells[cell.id],
                upM2S: m2sUpCells[cell.id],
            }
        }

        // GROUP block indexes (for label assignment)
        const gids: Record<string,number> = {}
        // NEXT AB-group item index
        let gindex = 0

        const sourcePaths = {}

        return {
            chain: chain,
            stereo: chain.lanes.every(lane => 
                !ChainLane.isSplit(lane)
                && lane.cells.filter(cell => !ChainCell.isStub(cell))
                    .filter(({items}) => items.filter(_ => !ChainCellItem.isEmpty(_)).every(item => item.L?.stereo))),
            invalid: !!Chain.invalid(chain,arch),
            lanes: chain.lanes
                // INSTANTIATE cell instances
                .map((lane:IChainLane,i:number) => ({
                    lane: lane.id,
                    stereo: ChainLane.isStereo(lane),
                    cells: [...lane.cells]
                        .map((cell,j) =>
                                // AB
                                cell.ab
                                ?  cell.items.map((item,k,items) => ({
                                        ...instantiate({lane,cell,cellIndex:j,item,itemIndex:k,laneIndex:i}),
                                        abgroup: {
                                            id: cell.id,
                                            midi: cell.midi,
                                            first: k === 0,
                                            last: k === items.length - 1,
                                            index: k,
                                            // XXX ASSIGN group index (skip stubbed AB-cells - they cannot be activated)
                                            gindex: ChainCellItem.isEmpty(item)
                                                ? null
                                                : gids[`${cell.id}_${k}`] != null
                                                ? gids[`${cell.id}_${k}`] 
                                                : gids[`${cell.id}_${k}`] = gindex++,
                                            // COUNT all non-stubbed AB-items
                                            count: cell.items.reduce((p,item) => p + (ChainCellItem.isEmpty(item) ? 0 : 1),0),
                                        }
                                    }))
                                // NON-AB
                                : [instantiate({lane,cell,cellIndex:j,item:cell.items[0],itemIndex:0,laneIndex:i})]
                    )
                }))
                // COMPILE source-paths (used later for cycle detection)
                ['tap']((lane:{cells:IMatrixCellItem[][]}) => {
                    // LANE SOURCE ID
                    const source = lane.cells[0][0].cell.id
                    // SOURCES of lane source
                    const sources:string[] = sourcePaths[source] || []
                    lane.cells.forEach((cells,j) => {
                        // COMPILE cell sources, if lane source this doesn't change,
                        // if non-source it's a compilation of lane source and its sources
                        const $sources = j === 0 ? [...sources] : [source,...sources]
                        // RECORD cell sources
                        sourcePaths[cells[0].cell.id] = $sources
                        cells.forEach(cell => cell.sources = $sources)
                    })
                })
                // EXPAND modules of each lane
                .map((lane:IMatrixLane) => ({
                    ...lane,
                    cells: lane.cells.flat()
                }))
                // MARK cell items "bypassed"
                ['tap']((lane:{cells:IMatrixCellItem[]}) => {
                    lane.cells.forEach(cell => {
                        const {id} = cell.cell
                        cell.bypassed = bypassed[id]
                    })
                })
                // MATERIALIZE lanes, respecting left-to-right SOURCE FLOW
                // (magic that materializes horizontal span connections in chain visualization)
                .reduce((lanes:IMatrixLane[],lane:IMatrixLane,i:number) : IMatrixLane[] => {
                    // FIRST lane
                    const first = i === 0
                    const source = lane.cells[0] as IMatrixCellItem
                    ///////////////////////////////////////////////////////////////////
                    // DISCOVER lane's source tributary, if chain's source, it's itself
                    ///////////////////////////////////////////////////////////////////
                    // TRIBUTARY's home lane
                    const home = first
                        // CHAIN SOURCE
                        ? {cells:[]}
                        // SEARCH right-ward across chain for first occurrence
                        : lanes.find(lane => lane.cells.find(cell => !cell.fill && cell.cell.id === source.cell.id))
                    if (home == null) {
                        D('CANNOT find HOME LANE of SOURCE',source)
                        throw 'missing SOURCE home lane'
                    }
                    // TRIBUTARY's home row - lane's source will position itself directly beneath
                    const row = first ? 0 : home.cells.findIndex(cell => !cell.fill && cell.cell.id === source.cell.id)
                    if (row === -1) D('MISSING SOURCE HOME',i,lane)

                    // POPULATE lane's top with all *non-lane* sources
                    // (i.e. those flowing right-ward to serve as sources for successive lanes)
                    // LANE's SOURCE resides directly beneath this
                    const $lane = {
                        ...lane,
                        cells: home
                            .cells
                            .slice(0,row)
                            // OVERRIDE `lane source` property, these sources are just "passing thru" (left->right)
                            .map(cell => ({...cell,$source:false} as MatrixSlot)),
                    }
                    // APPEND remaining lane contents
                    $lane.cells.push(...lane.cells)
                    lanes.push($lane)
                    return lanes
                },[])
                // above, we populated top of each lane with sources, at some point these terminate
                // and but they remain resident in right-ward lanes after termination
                // so we "blank" these instances out
                // XXX should we be doing this in the "magic" section above???
                .tap((lane:IMatrixLane,i:number) => {
                    for (let j = 0; j < lane.cells.length; j++) {
                        const c = lane.cells[j] as IMatrixCellItem
                        // LOCATED a source that does not track this far right, replace with "fill"
                        if (i > tracks.get(c._id)!) { 
                            lane.cells[j] = {
                                _id: Identity.create(),
                                fill: true,
                                split: lane.cells[j].split,
                                lane: lane.cells[j].lane,
                                ...extractRole({source:true}),
                                m2s: lane.cells[j].m2s,
                                upM2S: lane.cells[j].upM2S,
                            } as IMatrixFill
                        }
                    }
                })
                // DISCOVER longest dest module
                .tap((lane:IMatrixLane) => $LH = lane.cells.filter(c => c.dest).length > $LH
                    ? lane.cells.filter(c => c.dest).length
                    : $LH)
                // DISCOVER longest lane (sans destinations)
                .tap((lane:IMatrixLane) => $GH = lane.cells.filter(c => !c.dest).length > $GH
                    ? lane.cells.filter(c => !c.dest).length
                    : $GH)
                // NORMALIZE length of lanes with "dest" fill
                .tap((lane:IMatrixLane) => {
                    // CHAIN height
                    const H = $GH + $LH
                    // EXTRACT lane destination(s - including fills), we move these further down chain (after DEST_FILL)
                    const dests = lane.cells.filter(({dest}) => dest)
                    // REMOVE dests
                    lane.cells.length = lane.cells.length - dests.length
                    // FILL (DEST)
                    const {split,m2s,upM2S} = dests[0]
                    const fills = new Array<IMatrixFill>(H - lane.cells.length - dests.length)
                        .fill({
                            fill: true,
                            dest: true,
                            lane: {
                                id: lane.lane,
                                stereo: lane.stereo,
                            },
                            split,
                            m2s,
                            upM2S,
                        })
                    lane.cells.push(...fills)
                    // APPEND dests back
                    lane.cells.push(...dests)
                })
                .reduce((lanes: IMatrixLane[],lane:IMatrixLane) : IMatrixLane[] => {
                    lanes.push({...lane})
                    return lanes
                },[])
        }
    } catch (e) {
        D(e)
        D('OFFENDING CHAIN',chain)
        return {
            invalid: true,
            chain,
            lanes:[]
        }
    }
}
